package org.maplibre.compose.camera.internal

import androidx.compose.ui.unit.DpOffset
import kotlin.time.Duration
import org.maplibre.compose.interaction.internal.CameraComponent

/** Applies camera permissions and start callbacks before backend commands. */
internal fun CameraInputTarget.inputPanBy(
  deltaX: Double,
  deltaY: Double,
  gestureToken: CameraInputToken?,
) {
  val token = gestureToken ?: return
  if ((deltaX != 0.0 || deltaY != 0.0) && token.prepare(CameraComponent.Pan))
    moveBy(deltaX, deltaY, gestureToken = token)
}

internal fun CameraInputTarget.inputScaleBy(
  scale: Double,
  anchor: DpOffset?,
  gestureToken: CameraInputToken?,
) {
  val token = gestureToken ?: return
  if (scale != 1.0 && token.prepare(CameraComponent.Zoom))
    scaleBy(scale, anchor.takeIf { token.permitted(CameraComponent.Pan) }, gestureToken = token)
}

internal fun CameraInputTarget.inputRotateAndPitchBy(
  bearingDelta: Double,
  pitchDelta: Double,
  anchor: DpOffset? = null,
  gestureToken: CameraInputToken?,
  feedback: Boolean = true,
) {
  val token = gestureToken ?: return
  val bearing =
    if (bearingDelta != 0.0 && token.prepare(CameraComponent.Rotate)) bearingDelta else 0.0
  val pitch = if (pitchDelta != 0.0 && token.prepare(CameraComponent.Tilt)) pitchDelta else 0.0
  if (token.acceptsCommands && (bearing != 0.0 || pitch != 0.0))
    rotateAndPitchBy(
      bearing,
      pitch,
      anchor = anchor.takeIf { token.permitted(CameraComponent.Pan) },
      gestureToken = token,
      feedback = feedback,
    )
}

internal suspend fun CameraInputTarget.inputPanByAwaitingTransition(
  deltaX: Double,
  deltaY: Double,
  duration: Duration,
  gestureToken: CameraInputToken,
) {
  if ((deltaX != 0.0 || deltaY != 0.0) && gestureToken.prepare(CameraComponent.Pan))
    moveByAwaitingTransition(deltaX, deltaY, duration, gestureToken)
}

internal suspend fun CameraInputTarget.inputScaleByAwaitingTransition(
  scale: Double,
  anchor: DpOffset?,
  duration: Duration,
  gestureToken: CameraInputToken,
) {
  if (scale != 1.0 && gestureToken.prepare(CameraComponent.Zoom))
    scaleByAwaitingTransition(
      scale,
      anchor.takeIf { gestureToken.permitted(CameraComponent.Pan) },
      duration,
      gestureToken,
    )
}

internal suspend fun CameraInputTarget.inputRotateAndPitchByAwaitingTransition(
  bearingDelta: Double,
  pitchDelta: Double,
  duration: Duration,
  gestureToken: CameraInputToken,
  anchor: DpOffset? = null,
) {
  val bearing =
    if (bearingDelta != 0.0 && gestureToken.prepare(CameraComponent.Rotate)) bearingDelta else 0.0
  val pitch =
    if (pitchDelta != 0.0 && gestureToken.prepare(CameraComponent.Tilt)) pitchDelta else 0.0
  if (gestureToken.acceptsCommands && (bearing != 0.0 || pitch != 0.0))
    rotateAndPitchByAwaitingTransition(
      bearing,
      pitch,
      duration,
      gestureToken,
      anchor.takeIf { gestureToken.permitted(CameraComponent.Pan) },
    )
}

internal suspend fun CameraInputTarget.inputFitBoundsAwaitingTransition(
  fit: BoxZoomFit,
  duration: Duration,
  gestureToken: CameraInputToken,
) {
  if (!gestureToken.permitted(CameraComponent.Pan) || !gestureToken.permitted(CameraComponent.Zoom))
    return
  if (gestureToken.prepare(CameraComponent.Pan) && gestureToken.prepare(CameraComponent.Zoom))
    fitBoundsAwaitingTransition(fit, duration, gestureToken)
}
